// Offline IP intelligence datasets for the IP and Domain Intelligence tool.
//
// PRIVACY NOTE: these functions DO reach the network, but they only ever
// download whole public lists — they never send an IP anywhere. Downloading
// every known VPN range reveals nothing about which address an investigator is
// actually looking at, which is what lets the VPN / datacenter / Tor / abuse
// verdicts stay confidential. Per-IP requests live in ipIntelLive.js and are
// opt-in.
//
// The lists are cached in IndexedDB so they survive a reload and keep working
// with the network off, and every verdict is reported alongside the date its
// dataset was fetched — a "this was a VPN IP" finding is only meaningful with
// an as-of date attached.

import { useCallback, useEffect, useRef, useState } from 'react';
import { cidrToRange, cidrToRangeV6, ipToUint32, ipv6ToBigInt, isValidIpv4, isValidIpv6 } from './ipIntel';

export const STALE_MS = 24 * 60 * 60 * 1000; // refresh in the background past this
const FETCH_TIMEOUT_MS = 30000;              // a stalled CDN must not hang the UI

// Every dataset download goes through here so a request that never settles
// turns into a visible error instead of a permanent "Updating…".
async function fetchText(url) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { headers: { Accept: '*/*' }, signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.text();
  } catch (err) {
    throw err.name === 'AbortError' ? new Error('Timed out') : err;
  } finally {
    clearTimeout(timer);
  }
}

export const DATASETS = {
  vpn: {
    id: 'vpn',
    label: 'VPN ranges',
    url: 'https://raw.githubusercontent.com/X4BNet/lists_vpn/main/output/vpn/ipv4.txt',
    attribution: 'X4BNet/lists_vpn',
    homepage: 'https://github.com/X4BNet/lists_vpn',
  },
  datacenter: {
    id: 'datacenter',
    label: 'Datacenter / hosting ranges',
    url: 'https://raw.githubusercontent.com/X4BNet/lists_vpn/main/output/datacenter/ipv4.txt',
    attribution: 'X4BNet/lists_vpn',
    homepage: 'https://github.com/X4BNet/lists_vpn',
  },
  threat: {
    id: 'threat',
    label: 'Abuse / threat reputation',
    url: 'https://raw.githubusercontent.com/stamparm/ipsum/master/ipsum.txt',
    attribution: 'IPsum (stamparm)',
    homepage: 'https://github.com/stamparm/ipsum',
  },
  // The X4BNet datacenter list has real gaps — it does not cover Cloudflare's
  // 104.16.0.0/12, for instance — so major hosting/CDN networks are also
  // checked directly by the address space their ASN announces. This both
  // closes the gap and lets the tool name the provider rather than only saying
  // "datacenter".
  cloud: {
    id: 'cloud',
    label: 'Major cloud / CDN networks',
    asns: [
      { asn: 13335, name: 'Cloudflare' },
      { asn: 16509, name: 'Amazon AWS' },
      { asn: 14618, name: 'Amazon AWS' },
      { asn: 8075,  name: 'Microsoft Azure' },
      { asn: 15169, name: 'Google' },
      { asn: 396982, name: 'Google Cloud' },
      { asn: 14061, name: 'DigitalOcean' },
      { asn: 20940, name: 'Akamai' },
      { asn: 63949, name: 'Akamai (Linode)' },
      { asn: 24940, name: 'Hetzner' },
      { asn: 16276, name: 'OVH' },
      { asn: 14421, name: 'Oracle Cloud' },
      { asn: 45102, name: 'Alibaba Cloud' },
    ],
    urlFor: asn => `https://raw.githubusercontent.com/ipverse/asn-ip/master/as/${asn}/ipv4-aggregated.txt`,
    attribution: 'ipverse/asn-ip',
    homepage: 'https://github.com/ipverse/asn-ip',
  },
  tor: {
    id: 'tor',
    label: 'Tor exit relays',
    url: 'https://onionoo.torproject.org/details?flag=Exit&type=relay&running=true'
       + '&fields=nickname,fingerprint,exit_addresses,or_addresses,first_seen,last_seen,as,as_name,country',
    attribution: 'Tor Project — Onionoo',
    homepage: 'https://metrics.torproject.org/onionoo.html',
  },
  // Apple publishes the address space its iCloud Private Relay egress nodes
  // use, precisely so firewalls and tools like this one don't have to guess.
  // It's the only offline dataset that covers IPv6 — the relay is what an
  // IPv6-heavy carrier network actually hands out for it — so it gets its own
  // BigInt-based range table below instead of the Uint32 one everything else
  // shares. RDAP/WHOIS on a relay address correctly names whichever CDN
  // partner (Fastly, Cloudflare, Akamai) holds that block, not Apple or
  // "Private Relay" — that branding only exists in curated lists like this one.
  //
  // Unlike every other dataset here, this is a bundled snapshot rather than a
  // live fetch: mask-api.icloud.com sends no Access-Control-Allow-Origin
  // header at all, so a browser refuses to read the response cross-origin no
  // matter how often it's "refreshed" — that endpoint is meant for firewall
  // admins' server-side scripts (see the homepage below), not client-side JS.
  // Serving our own copy from the same origin as the app sidesteps CORS
  // entirely. `snapshotDate` is when the file below was last regenerated —
  // display it, don't infer freshness from the fetch timestamp, since
  // "fetched just now" here only means "read our own bundled file," not
  // "got Apple's current list." To refresh it: curl the URL below, keep only
  // the CIDR column (`awk -F, '{print $1}'`), overwrite
  // public/data/icloud-relay-ranges.csv, and bump snapshotDate.
  appleRelay: {
    id: 'appleRelay',
    label: 'iCloud Private Relay egress ranges',
    url: '/data/icloud-relay-ranges.csv',
    sourceUrl: 'https://mask-api.icloud.com/egress-ip-ranges.csv',
    snapshotDate: '2026-09-06',
    attribution: 'Apple',
    homepage: 'https://support.apple.com/en-us/102602',
  },
};

const DATASET_IDS = Object.keys(DATASETS);

// ── IndexedDB (raw API — the app has no idb helper and doesn't need one) ──────

const DB_NAME = 'letools-ipintel';
const DB_VERSION = 1;
const STORE = 'datasets';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll() {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => { resolve(req.result || []); db.close(); };
    req.onerror = () => { reject(req.error); db.close(); };
  }));
}

function idbPut(record) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => { resolve(); db.close(); };
    tx.onerror = () => { reject(tx.error); db.close(); };
  }));
}

// Drops everything this tool has stored locally. Exported because a tool that
// caches ~1MB in someone's browser should offer a visible way to remove it —
// and because it's the only way to test a cold download.
export function deleteCachedDatasets() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { resolve(); return; }
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    // Nothing holds a long-lived connection (every helper closes its own), so
    // a block here means another tab — not worth failing over.
    req.onblocked = () => resolve();
  });
}

// ── Parsing ──────────────────────────────────────────────────────────────────

// Sorted, non-overlapping ranges collapse into a flat Uint32Array of
// [start, end, start, end, …] so a lookup is one binary search. Merging
// overlaps up front is what makes the search a plain "find the last start <= n"
// with no backtracking. Shared by every IPv4 CIDR list in this file.
function mergeRangesV4(ranges) {
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = new Uint32Array(ranges.length * 2);
  let len = 0;
  for (const [start, end] of ranges) {
    // Extend the previous range when this one touches or overlaps it.
    if (len > 0 && start <= merged[len - 1] + 1) {
      if (end > merged[len - 1]) merged[len - 1] = end;
    } else {
      merged[len++] = start;
      merged[len++] = end;
    }
  }
  return merged.subarray(0, len);
}

function parseCidrList(text) {
  const ranges = [];
  for (const line of String(text).split('\n')) {
    const s = line.trim();
    if (!s || s[0] === '#') continue;
    const r = cidrToRange(s);
    if (r) ranges.push(r);
  }
  return { table: mergeRangesV4(ranges), count: ranges.length };
}

function inRangeTable(table, n) {
  // Largest even index i where table[i] <= n.
  let lo = 0, hi = table.length / 2 - 1, hit = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (table[mid * 2] <= n) { hit = mid; lo = mid + 1; } else { hi = mid - 1; }
  }
  return hit >= 0 && n <= table[hit * 2 + 1];
}

// Same idea as mergeRangesV4, but for 128-bit BigInt bounds, which don't fit
// a typed array. Only the Apple relay dataset needs this.
function mergeRangesV6(ranges) {
  ranges.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  const merged = [];
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1] + 1n) {
      if (end > last[1]) last[1] = end;
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

function inRangeTableV6(ranges, n) {
  let lo = 0, hi = ranges.length - 1, hit = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (ranges[mid][0] <= n) { hit = mid; lo = mid + 1; } else { hi = mid - 1; }
  }
  return hit >= 0 && n <= ranges[hit][1];
}

// Apple's CSV is "<cidr>,<country>,<region>,<city>," — one CIDR per line, v4
// and v6 mixed. The tool only needs the yes/no flag, not the per-city
// breakdown, so both address families just collapse into one merged range
// table each rather than keeping every row's metadata around.
function parseAppleRelayList(text) {
  const v4Ranges = [];
  const v6Ranges = [];
  for (const line of String(text).split('\n')) {
    const comma = line.indexOf(',');
    const cidr = (comma < 0 ? line : line.slice(0, comma)).trim();
    if (!cidr) continue;
    if (cidr.includes(':')) {
      const r = cidrToRangeV6(cidr);
      if (r) v6Ranges.push(r);
    } else {
      const r = cidrToRange(cidr);
      if (r) v4Ranges.push(r);
    }
  }
  return {
    v4: mergeRangesV4(v4Ranges),
    v6: mergeRangesV6(v6Ranges),
    count: v4Ranges.length + v6Ranges.length,
  };
}

function appleRelayLookup(index, ip) {
  if (isValidIpv4(ip)) return inRangeTable(index.v4, ipToUint32(ip));
  if (isValidIpv6(ip)) {
    const n = ipv6ToBigInt(ip);
    return n === null ? null : inRangeTableV6(index.v6, n);
  }
  return null;
}

// The cloud dataset is cached as "<provider>\t<cidr>" lines, one file's worth
// of ASN prefixes after another. Ranges from different providers must not be
// merged, so instead of merging we keep a running maximum of the end addresses
// seen so far — that lets the lookup walk back over any overlap and still
// terminate immediately in the common case.
function parseCloudList(text) {
  const entries = [];
  for (const line of String(text).split('\n')) {
    const tab = line.indexOf('\t');
    if (tab < 0) continue;
    const provider = line.slice(0, tab);
    const range = cidrToRange(line.slice(tab + 1).trim());
    if (range) entries.push([range[0], range[1], provider]);
  }
  entries.sort((a, b) => a[0] - b[0]);

  const starts = new Uint32Array(entries.length);
  const ends = new Uint32Array(entries.length);
  const maxEnd = new Uint32Array(entries.length);
  const providers = new Array(entries.length);
  let running = 0;
  entries.forEach(([start, end, provider], i) => {
    starts[i] = start;
    ends[i] = end;
    providers[i] = provider;
    if (end > running) running = end;
    maxEnd[i] = running;
  });

  return { starts, ends, maxEnd, providers, count: entries.length };
}

function cloudLookup(index, n) {
  const { starts, ends, maxEnd, providers } = index;
  let lo = 0, hi = starts.length - 1, hit = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid] <= n) { hit = mid; lo = mid + 1; } else { hi = mid - 1; }
  }
  for (let i = hit; i >= 0 && maxEnd[i] >= n; i--) {
    if (ends[i] >= n && starts[i] <= n) return providers[i];
  }
  return null;
}

// IPsum is "<ip>\t<number of blocklists>". Pack each entry into one float as
// ip * 256 + count (exact well under 2^53) so a single typed-array sort orders
// both halves together, then split them back out.
function parseThreatList(text) {
  const lines = String(text).split('\n');
  const packed = new Float64Array(lines.length);
  let n = 0;
  for (const line of lines) {
    if (!line || line[0] === '#') continue;
    const tab = line.indexOf('\t');
    if (tab < 0) continue;
    const ip = ipToUint32(line.slice(0, tab));
    if (ip === null) continue;
    const count = Math.min(255, parseInt(line.slice(tab + 1), 10) || 0);
    packed[n++] = ip * 256 + count;
  }
  const view = packed.subarray(0, n);
  view.sort();

  const ips = new Uint32Array(n);
  const counts = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const v = view[i];
    const count = v % 256;
    ips[i] = (v - count) / 256;
    counts[i] = count;
  }
  return { ips, counts, count: n };
}

function threatLookup(index, n) {
  const { ips, counts } = index;
  let lo = 0, hi = ips.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (ips[mid] === n) return counts[mid];
    if (ips[mid] < n) lo = mid + 1; else hi = mid - 1;
  }
  return 0;
}

// Onionoo gives us running exit relays with full provenance. Index by every
// address the relay is known by — exit_addresses is where traffic leaves from,
// which is what shows up in a victim's logs, but or_addresses matters too.
function parseTorList(text) {
  const byAddress = new Map();
  let relays = [];
  let published = null;
  try {
    const json = JSON.parse(text);
    relays = Array.isArray(json.relays) ? json.relays : [];
    published = json.relays_published || null;
  } catch {
    return { byAddress, count: 0, published: null };
  }

  // One address commonly hosts several relays. Keep the most informative
  // record (an exit beats an OR-address-only entry) but track how many relays
  // share the address, so the tool doesn't silently hide the others.
  const record = (key, next) => {
    const existing = byAddress.get(key);
    if (!existing) { byAddress.set(key, { ...next, relayCount: 1 }); return; }
    if (next.isExitAddress && !existing.isExitAddress) {
      byAddress.set(key, { ...next, relayCount: existing.relayCount + 1 });
    } else {
      existing.relayCount += 1;
    }
  };

  for (const relay of relays) {
    const info = {
      nickname: relay.nickname || '(unnamed)',
      fingerprint: relay.fingerprint || '',
      firstSeen: relay.first_seen || '',
      lastSeen: relay.last_seen || '',
      as: relay.as || '',
      asName: relay.as_name || '',
      country: (relay.country || '').toUpperCase(),
      isExitAddress: false,
    };
    for (const addr of relay.exit_addresses || []) {
      record(addr.toLowerCase(), { ...info, isExitAddress: true });
    }
    for (const addr of relay.or_addresses || []) {
      // "1.2.3.4:9001" or "[2001:db8::1]:9001"
      const host = addr.replace(/^\[/, '').replace(/\]:\d+$/, '').replace(/:\d+$/, '').toLowerCase();
      record(host, info);
    }
  }
  return { byAddress, count: relays.length, published };
}

// Exported so the parsers can be exercised directly against real list data.
export function buildIndex(id, text) {
  if (id === 'threat') return parseThreatList(text);
  if (id === 'tor') return parseTorList(text);
  if (id === 'cloud') return parseCloudList(text);
  if (id === 'appleRelay') return parseAppleRelayList(text);
  return parseCidrList(text);
}

// The cloud dataset is many small per-ASN files; fetch them together and store
// the result as one provider-tagged text blob so it caches like the others.
async function fetchCloudText(spec) {
  const parts = await Promise.all(spec.asns.map(async ({ asn, name }) => {
    try {
      const body = await fetchText(spec.urlFor(asn));
      return body
        .split('\n')
        .map(l => l.trim())
        .filter(l => l && l[0] !== '#')
        .map(cidr => `${name}\t${cidr}`)
        .join('\n');
    } catch {
      return ''; // one unreachable ASN file shouldn't lose the other twelve
    }
  }));
  const text = parts.filter(Boolean).join('\n');
  if (!text) throw new Error('No ASN prefix files could be downloaded');
  return text;
}

// ── Lookup ───────────────────────────────────────────────────────────────────

// Answers entirely from the in-memory indexes. `indexes` may be partial (a
// dataset that failed to load is simply reported as unknown rather than as a
// negative — an absent list must never read as "clean").
export function lookupLocal(ip, indexes) {
  const supported = isValidIpv4(ip);
  const n = supported ? ipToUint32(ip) : null;
  const torHit = indexes.tor ? indexes.tor.byAddress.get(String(ip).toLowerCase()) : undefined;
  const cloudProvider = supported && indexes.cloud ? cloudLookup(indexes.cloud, n) : null;
  const inDatacenterList = supported && indexes.datacenter ? inRangeTable(indexes.datacenter.table, n) : null;

  return {
    // IPv4-only, because the CIDR tables are Uint32Array-based.
    ipv4Supported: supported,
    isVpn:        supported && indexes.vpn ? inRangeTable(indexes.vpn.table, n) : null,
    // A named cloud provider is itself proof of hosting, so it counts even when
    // the general datacenter list misses the range (it misses Cloudflare's).
    isDatacenter: cloudProvider ? true : inDatacenterList,
    cloudProvider,
    threatListCount: supported && indexes.threat ? threatLookup(indexes.threat, n) : null,
    // Tor is matched by address string, so it works for IPv6 too.
    isTor: indexes.tor ? Boolean(torHit) : null,
    torRelay: torHit || null,
    // The one offline check that isn't IPv4-only — Apple's relay egress space
    // includes plenty of IPv6, and that's exactly where a carrier's mobile
    // subscribers are most likely to land.
    isAppleRelay: indexes.appleRelay ? appleRelayLookup(indexes.appleRelay, ip) : null,
  };
}

// ── React hook ───────────────────────────────────────────────────────────────

// Loads the cached copy first so the tool is usable immediately, then refreshes
// in the background if anything is older than STALE_MS. A failed refresh keeps
// the stale copy and surfaces its real age rather than silently pretending to
// be current.
export function useIpDatasets() {
  const [indexes, setIndexes] = useState({});
  const [meta, setMeta] = useState({});          // id -> { fetchedAt, count, error }
  const [status, setStatus] = useState('loading'); // loading | ready | refreshing | empty
  const mounted = useRef(true);

  // React 18 StrictMode mounts, unmounts, then remounts in development. The
  // cleanup must not leave this latched false on the remount, or every later
  // setState is skipped and the UI hangs on "Updating…" forever.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const ingest = useCallback((id, text, fetchedAt) => {
    let index;
    try {
      index = buildIndex(id, text);
    } catch (err) {
      setMeta(m => ({ ...m, [id]: { fetchedAt, count: 0, error: 'Could not parse' } }));
      return;
    }
    if (!mounted.current) return;
    setIndexes(prev => ({ ...prev, [id]: index }));
    setMeta(m => ({ ...m, [id]: { fetchedAt, count: index.count, error: null } }));
  }, []);

  const fetchOne = useCallback(async (id) => {
    const spec = DATASETS[id];
    try {
      const text = spec.asns ? await fetchCloudText(spec) : await fetchText(spec.url);
      const fetchedAt = Date.now();
      await idbPut({ id, text, fetchedAt }).catch(() => {}); // cache is best-effort
      ingest(id, text, fetchedAt);
      return true;
    } catch (err) {
      if (mounted.current) {
        setMeta(m => ({
          ...m,
          // Keep any existing fetchedAt: the old data is still loaded and usable.
          [id]: { ...(m[id] || { count: 0 }), error: err.message || 'Download failed' },
        }));
      }
      return false;
    }
  }, [ingest]);

  const refresh = useCallback(async (ids = DATASET_IDS) => {
    setStatus('refreshing');
    try {
      await Promise.all(ids.map(fetchOne));
    } finally {
      // Always leave the refreshing state, even on an unexpected throw —
      // otherwise the button stays disabled and the chip reads "Updating…".
      if (mounted.current) setStatus('ready');
    }
  }, [fetchOne]);

  // Wipe the cache and drop back to the empty state WITHOUT refetching, so the
  // next Refresh (or reload) exercises a genuine cold download.
  const clear = useCallback(async () => {
    try {
      await deleteCachedDatasets();
    } catch {
      // Even if the delete failed, clearing in-memory state is still correct.
    }
    if (!mounted.current) return;
    setIndexes({});
    setMeta({});
    setStatus('empty');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cached = [];
      try {
        cached = await idbGetAll();
      } catch {
        // No IndexedDB (private mode, locked-down browser) — fetch fresh instead.
      }
      if (cancelled) return;

      for (const rec of cached) {
        if (DATASETS[rec.id]) ingest(rec.id, rec.text, rec.fetchedAt);
      }

      const now = Date.now();
      const cachedById = Object.fromEntries(cached.map(r => [r.id, r]));
      const needed = DATASET_IDS.filter(id => {
        const rec = cachedById[id];
        return !rec || now - rec.fetchedAt > STALE_MS;
      });

      if (!mounted.current) return;
      setStatus(cached.length ? 'ready' : 'empty');
      if (needed.length) refresh(needed);
    })();
    return () => { cancelled = true; };
  }, [ingest, refresh]);

  const stamps = DATASET_IDS.map(id => meta[id] && meta[id].fetchedAt).filter(Boolean);
  const oldestFetchedAt = stamps.length === DATASET_IDS.length ? Math.min(...stamps) : (stamps[0] || null);
  const loadedCount = DATASET_IDS.filter(id => indexes[id]).length;

  return {
    indexes,
    meta,
    status,
    refresh: () => refresh(),
    clear,
    ready: loadedCount > 0,
    complete: loadedCount === DATASET_IDS.length,
    oldestFetchedAt,
    isStale: oldestFetchedAt !== null && Date.now() - oldestFetchedAt > STALE_MS,
  };
}

export function formatAge(ts) {
  if (!ts) return 'never';
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}
